import { MessageResponseDto } from "@api/api";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import { getPublicServiceFormDefault } from "@factory/form/form.default";
import {
  UtilServicePublicFormRule,
  UtilServicePublicFormSchema,
} from "@factory/form/form.rule";
import { useQyGetPublicUtilService } from "@factory/query/util-service.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { LabelValue } from "@shared/models/label-value.interface";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useCallback, useRef, useState } from "react";
import { FieldErrors, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

type Procedure = {
  code: string;
  name: string;
  description: string;
  email?: string;
  mobileNumber?: string;
  postRegistrationLink?: string;
};

type ContactInfo = {
  email: string;
  phone: string;
  postRegistrationLink?: string;
};

export function useGetPublicService() {
  // LOCAL DECLARATION
  const navigate = useNavigate();
  const { mapGetPublicUtilService } = useFormApiMapper();
  const { showError } = useNotificationContext();
  const [services, setServices] = useState<Procedure[]>([]);
  const [contactInfo, setContactInfo] = useState<ContactInfo>({
    email: "",
    phone: "",
    postRegistrationLink: undefined,
  });
  const { client } = useParams();
  const hasInitialized = useRef(false);

  // QUERIES
  const handleApiSuccess = (response: MessageResponseDto) => {
    const data = response.data as any;
    const servicesArray = (data?.services || data || []) as Procedure[];

    setServices(servicesArray);

    const firstService = servicesArray[0];
    if (firstService) {
      const newContactInfo: ContactInfo = {
        email: firstService.email || "support@clinic.com",
        phone: firstService.mobileNumber || "+63 912 345 6789",
        postRegistrationLink: firstService.postRegistrationLink,
      };
      setContactInfo(newContactInfo);
    }
  };
  const { mutate: getPublicUtilService, isLoading: isFetchingServices } =
    useQyGetPublicUtilService(handleApiSuccess);

  // LOCAL FUNCTIONS
  const formMethod = useForm<UtilServicePublicFormSchema>({
    defaultValues: getPublicServiceFormDefault,
    resolver: zodResolver(UtilServicePublicFormRule),
  });
  const { handleSubmit } = formMethod;

  const fetchServices = useCallback(() => {
    const form: UtilServicePublicFormSchema = {
      tenant: client,
    };
    const formData = mapGetPublicUtilService(form);
    getPublicUtilService(formData);
  }, [client, getPublicUtilService, mapGetPublicUtilService]);

  if (!hasInitialized.current && client && !isFetchingServices) {
    hasInitialized.current = true;
    fetchServices();
  }

  const handleValidateError = (
    err: FieldErrors<UtilServicePublicFormSchema>
  ) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };

  const serviceOptions = LabelValue.MapArray(
    services,
    (label) => label.name || "-",
    (value) => value.code
  );

  return {
    isFetchingServices,
    formMethod,
    navigate,
    handleSubmit,
    handleValidateError,
    getPublicUtilService,
    services,
    contactInfo,
    serviceOptions,
    fetchServices,
  };
}
